import type { ExampleComment } from './exampleComments'

export type ExampleReply = {
  id: string
  text: string
  publishedAt: string
}

export type ExampleCommentState = {
  status: 'unreplied' | 'replied' | 'dismissed'
  liked: boolean
  replies: ExampleReply[]
}

export type ExampleReplayState = Record<string, ExampleCommentState>

export type ExampleReplayAction =
  | { type: 'reply'; commentId: string; text: string }
  | { type: 'toggleLike'; commentId: string }
  | { type: 'resolve'; commentId: string }
  | { type: 'reopen'; commentId: string }

export function createExampleReplayState(
  comments: ExampleComment[],
): ExampleReplayState {
  return Object.fromEntries(
    comments.map((comment) => [
      comment.id,
      { status: 'unreplied', liked: false, replies: [] },
    ]),
  )
}

export function exampleReplayReducer(
  state: ExampleReplayState,
  action: ExampleReplayAction,
): ExampleReplayState {
  const current = state[action.commentId]
  if (!current) return state

  switch (action.type) {
    case 'reply':
      return {
        ...state,
        [action.commentId]: {
          ...current,
          status: 'replied',
          replies: [
            ...current.replies,
            {
              id: `${action.commentId}-reply-${current.replies.length + 1}`,
              text: action.text,
              publishedAt: new Date().toISOString(),
            },
          ],
        },
      }
    case 'toggleLike':
      return {
        ...state,
        [action.commentId]: { ...current, liked: !current.liked },
      }
    case 'resolve':
      return {
        ...state,
        [action.commentId]: { ...current, status: 'dismissed' },
      }
    case 'reopen':
      return {
        ...state,
        [action.commentId]: {
          ...current,
          status: current.replies.length > 0 ? 'replied' : 'unreplied',
        },
      }
  }
}
